// 529 money already withdrawn and tied to no expense (ZEN-785).
//
// The figure is the server's (`untied_usd` on GET /withdrawals, from
// distribution_backing.untied_usd); this file only reads it. Used by the
// Expenses page footer and the new-withdrawal form, so both say the same thing.
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";

/** The beneficiary's withdrawals, on the same cache the Distributions page uses. */
export function useStudentWithdrawals(studentId, { enabled = true } = {}) {
  return useQuery({
    queryKey: ["withdrawals", studentId],
    queryFn: async () => (await api.get(`/withdrawals?student_id=${studentId}`)).data,
    // Read as a list or not at all: a reminder is never drawn from anything else.
    select: (data) => (Array.isArray(data) ? data : []),
    enabled: !!studentId && enabled,
  });
}

export const untiedOf = (withdrawals = []) =>
  withdrawals.filter((w) => Number(w.untied_usd) > 0.005);

/** The tax year the untied money can still be tied in. */
export function untiedYear(w) {
  if (Number.isInteger(w?.eligibility_tax_year)) return w.eligibility_tax_year;
  const d = w?.date_received || w?.date_requested || "";
  return /^\d{4}/.test(d) ? Number(d.slice(0, 4)) : null;
}

/** The untied withdrawals for the tax year on screen, and the other years
 *  still holding untied money (ZEN-790, Maryse's option B, 20 Oct 2026).
 *  With no year picked, every one shows. A withdrawal whose year cannot be
 *  told always shows, so nothing is hidden by a filter it cannot match. */
export function untiedForYear(withdrawals = [], year) {
  const all = untiedOf(withdrawals);
  if (!year) return { shown: all, otherYears: [] };
  const want = Number(year);
  const shown = [];
  const counts = new Map();
  for (const w of all) {
    const y = untiedYear(w);
    if (y === null || y === want) shown.push(w);
    else counts.set(y, (counts.get(y) || 0) + 1);
  }
  const otherYears = [...counts].sort((a, b) => a[0] - b[0]).map(([y, n]) => ({ year: y, count: n }));
  return { shown, otherYears };
}

// Opening ONE withdrawal in its edit form from another page.
export const EDIT_WITHDRAWAL_KEY = "editWithdrawalId";
export const editWithdrawalNavState = (id) => ({ [EDIT_WITHDRAWAL_KEY]: id });
